import { Cashflow } from '../types/cashflow';
import { Participant } from '../types/vehicle';

/**
 * 참여자 계정 — 한 자금원 안에서 참여자마다 얼마를 약정했고 · 넣었고 · 돌려받았나.
 *
 * 다른 플랫폼이 「조합 계정(Capital Account)」이라 부르는 것의 가장 작은 모양이다
 * (`docs/02-design/010-gap-analysis.md` 2-2). 손익 배분까지는 하지 않는다 —
 * 분배 순서가 규약에 달려 있어 아직 모른다.
 *
 * ⚠️ 납입액을 저장하지 않는다. 원장의 `contribution` 합계가 납입액이다.
 */
export interface CapitalRow {
  p: Participant;
  committed: number | null;
  /** 실제로 넣은 돈 */
  paidIn: number;
  /** 약정 − 납입. 약정을 모르면 null */
  unpaid: number | null;
  /** 돌려받은 돈 */
  distributed: number;
  /** 이 참여자 앞으로 적힌 보수 — GRK 가 운용해서 받은 돈 */
  fee: number;
  /**
   * 분배 비율. 규약이 정한 값(`shareRatio`)이 있으면 그것. 없으면 **규약이 남긴 몫**
   * (1 − 규약 비율 합)을 규약 없는 참여자끼리 납입 비율로 나눈다 — 합이 100% 가 되게.
   * 납입이 없으면 null
   */
  share: number | null;
  /** 비율이 어디서 왔나 */
  shareFrom: 'rule' | 'paid' | null;
}

export function capitalRows(parts: Participant[], flows: Cashflow[]): CapitalRow[] {
  const sumOf = (pid: number, kind: string) =>
    flows.filter((f) => f.participantId === pid && f.kind === kind).reduce((s, f) => s + f.amount, 0);

  // 규약 비율이 없는 참여자끼리만 납입 비율을 낸다 — 규약 몫과 섞으면 합이 100% 를 넘는다
  const free = parts.filter((p) => p.shareRatio === null);
  const paidTotal = free.reduce((s, p) => s + sumOf(p.id, 'contribution'), 0);
  const left = Math.max(0, 1 - parts.reduce((s, p) => s + (p.shareRatio ?? 0), 0));

  return parts.map((p) => {
    const paidIn = sumOf(p.id, 'contribution');
    const committed = p.committedAmount;
    const share =
      p.shareRatio !== null ? p.shareRatio : paidTotal > 0 ? (left * paidIn) / paidTotal : null;
    return {
      p,
      committed,
      paidIn,
      unpaid: committed === null ? null : Math.max(0, committed - paidIn),
      distributed: sumOf(p.id, 'distribution'),
      fee: sumOf(p.id, 'fee'),
      share,
      shareFrom: p.shareRatio !== null ? 'rule' : paidTotal > 0 ? 'paid' : null,
    };
  });
}

/** 원장 기준 통장 잔액 — 들어온 돈 − 나간 돈. 실제 통장과 맞춰 볼 숫자다 */
export function ledgerBalance(flows: Cashflow[]): number {
  return flows.reduce((s, f) => s + (f.direction === 'in' ? f.amount : -f.amount), 0);
}
